import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, CheckCircle2, Clock, DollarSign, ShieldCheck, 
  Phone, Mail, MapPin 
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const GetOfferPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formspree.io/f/meepwbqw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          formType: 'Get Your Offer - Home Sale',
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          propertyAddress: formData.address,
          additionalInfo: formData.description,
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert('Something went wrong. Please try again or call us directly.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Clock,
      text: 'Get your offer within 24 hours',
    },
    {
      icon: DollarSign,
      text: 'No fees or commissions',
    },
    {
      icon: ShieldCheck,
      text: 'No obligation to accept',
    },
  ];

  return (
    <div data-testid="get-offer-page" className="pt-20">
      {/* Hero Section with Gradient */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white via-emerald/5 to-white relative overflow-hidden">
        {/* Floating background elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div 
            className="absolute top-20 right-20 w-72 h-72 bg-emerald/10 rounded-full blur-3xl"
            animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-20 left-20 w-72 h-72 bg-gold/10 rounded-full blur-3xl"
            animate={{ y: [0, 20, 0], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Left Content */}
            <AnimatedSection direction="left">
              <motion.span 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-block bg-emerald/10 text-emerald font-subheading font-medium text-sm px-4 py-2 rounded-full mb-6"
              >
                Get Started Today
              </motion.span>
              <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-darkgreen mb-6">
                Get Your Cash Offer Today
              </h1>
              <p className="font-subheading text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                Fill in your property details and we'll contact you within 24 hours 
                with a fair, no-obligation cash offer.
              </p>

              {/* Benefits with animations */}
              <div className="space-y-4 mb-10">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                    whileHover={{ x: 5 }}
                  >
                    <motion.div 
                      className="w-10 h-10 bg-emerald/10 rounded-xl flex items-center justify-center"
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(4, 99, 7, 0.2)' }}
                    >
                      <benefit.icon className="w-5 h-5 text-emerald" />
                    </motion.div>
                    <span className="font-body text-gray-700">{benefit.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Contact Info Card */}
              <motion.div 
                className="bg-darkgreen rounded-2xl p-8 text-white relative overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                {/* Decorative element */}
                <motion.div 
                  className="absolute top-0 right-0 w-32 h-32 bg-emerald/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
                
                <h3 className="relative font-heading font-bold text-xl mb-6">
                  Prefer to Talk?
                </h3>
                <div className="relative space-y-4">
                  <motion.div 
                    className="flex items-center gap-3"
                    whileHover={{ x: 5 }}
                  >
                    <Phone className="w-5 h-5 text-gold" />
                    <span>+1 307-298-2879</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-3"
                    whileHover={{ x: 5 }}
                  >
                    <Mail className="w-5 h-5 text-gold" />
                    <span>info@greenpalmconsulting.com</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-start gap-3"
                    whileHover={{ x: 5 }}
                  >
                    <MapPin className="w-5 h-5 text-gold mt-0.5" />
                    <span>30 N Gould St Ste R<br />Sheridan, WY 82801</span>
                  </motion.div>
                </div>
              </motion.div>
            </AnimatedSection>

            {/* Form */}
            <AnimatedSection direction="right" delay={0.2}>
              <motion.div 
                className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 border border-gray-100 relative overflow-hidden"
                whileHover={{ boxShadow: '0 30px 60px rgba(10, 61, 45, 0.15)' }}
              >
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald/5 via-transparent to-gold/5 pointer-events-none" />
                
                <AnimatePresence mode="wait">
                  {!isSubmitted ? (
                    <motion.div
                      key="form"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="relative"
                    >
                      <div className="mb-8">
                        <h2 className="font-heading text-2xl font-bold text-darkgreen mb-2">
                          Property Information
                        </h2>
                        <p className="font-body text-gray-500 text-sm">
                          All fields marked with * are required
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Name */}
                        <motion.div 
                          className="space-y-2"
                          whileHover={{ scale: 1.01 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Label htmlFor="name" className="text-darkgreen font-medium">
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            required
                            placeholder="John Smith"
                            value={formData.name}
                            onChange={handleChange}
                            data-testid="form-name-input"
                            className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50 transition-all duration-300"
                          />
                        </motion.div>

                        {/* Phone */}
                        <motion.div 
                          className="space-y-2"
                          whileHover={{ scale: 1.01 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Label htmlFor="phone" className="text-darkgreen font-medium">
                            Phone Number *
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            required
                            placeholder="(555) 123-4567"
                            value={formData.phone}
                            onChange={handleChange}
                            data-testid="form-phone-input"
                            className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50 transition-all duration-300"
                          />
                        </motion.div>

                        {/* Email */}
                        <motion.div 
                          className="space-y-2"
                          whileHover={{ scale: 1.01 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Label htmlFor="email" className="text-darkgreen font-medium">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            placeholder="john@example.com"
                            value={formData.email}
                            onChange={handleChange}
                            data-testid="form-email-input"
                            className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50 transition-all duration-300"
                          />
                        </motion.div>

                        {/* Property Address */}
                        <motion.div 
                          className="space-y-2"
                          whileHover={{ scale: 1.01 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Label htmlFor="address" className="text-darkgreen font-medium">
                            Property Address *
                          </Label>
                          <Input
                            id="address"
                            name="address"
                            type="text"
                            required
                            placeholder="123 Main St, City, State ZIP"
                            value={formData.address}
                            onChange={handleChange}
                            data-testid="form-address-input"
                            className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50 transition-all duration-300"
                          />
                        </motion.div>

                        {/* Description */}
                        <motion.div 
                          className="space-y-2"
                          whileHover={{ scale: 1.01 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Label htmlFor="description" className="text-darkgreen font-medium">
                            Additional Information (Optional)
                          </Label>
                          <Textarea
                            id="description"
                            name="description"
                            placeholder="Tell us about your property, timeline, or any special circumstances..."
                            value={formData.description}
                            onChange={handleChange}
                            data-testid="form-description-input"
                            className="min-h-[120px] rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50 resize-none transition-all duration-300"
                          />
                        </motion.div>

                        {/* Submit Button */}
                        <motion.button
                          type="submit"
                          disabled={isSubmitting}
                          data-testid="form-submit-button"
                          className="btn-premium w-full bg-emerald hover:bg-emerald-dark text-white font-bold text-lg px-8 py-5 rounded-full disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {isSubmitting ? (
                            <>
                              <motion.svg 
                                className="h-5 w-5" 
                                viewBox="0 0 24 24"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                  fill="none"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </motion.svg>
                              Submitting...
                            </>
                          ) : (
                            <>
                              Get My Cash Offer
                              <ArrowRight className="w-5 h-5" />
                            </>
                          )}
                        </motion.button>

                        <p className="text-center text-xs text-gray-500 mt-4">
                          By submitting, you agree to our{' '}
                          <a href="/privacy" className="text-emerald hover:underline">
                            Privacy Policy
                          </a>{' '}
                          and{' '}
                          <a href="/terms" className="text-emerald hover:underline">
                            Terms of Service
                          </a>
                        </p>
                      </form>
                    </motion.div>
                  ) : (
                    /* Success State */
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-8 relative"
                      data-testid="form-success-message"
                    >
                      <motion.div 
                        className="w-20 h-20 bg-emerald/10 rounded-full flex items-center justify-center mx-auto mb-6"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                      >
                        <CheckCircle2 className="w-10 h-10 text-emerald" />
                      </motion.div>
                      <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                        Thank You!
                      </h2>
                      <p className="font-body text-gray-600 mb-6">
                        We've received your information. Our team will review your 
                        property details and contact you within 24 hours with a cash offer.
                      </p>
                      <div className="bg-gray-50 rounded-xl p-6">
                        <p className="text-sm text-gray-500 mb-2">What happens next?</p>
                        <ul className="text-left space-y-2">
                          {[
                            'Our team reviews your property information',
                            'We prepare a fair cash offer based on market analysis',
                            'We contact you to discuss the offer – no obligation',
                          ].map((item, index) => (
                            <motion.li 
                              key={index}
                              className="flex items-start gap-2 text-sm text-gray-700"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.4 + index * 0.1 }}
                            >
                              <CheckCircle2 className="w-4 h-4 text-emerald mt-0.5 flex-shrink-0" />
                              {item}
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetOfferPage;
